import React from 'react'
import * as blueprint from '@adc/blueprint'
import { enums } from '@adc/sdk'
import { formatMoney } from '../../../../utils/formatters'
import ScrollSpy from '../../../layouts/scrollspy'
import ListingBadge, { BADGE_MODE } from './base'
import { isPrivateSeller } from './utils'

const isExpandedPrivateSeller = (listing, badgeMode) =>
  badgeMode === BADGE_MODE.EXPANDED && isPrivateSeller(listing)

// Option Period is TX/GA-specific real-estate terminology.
const OPTION_PERIOD_STATES = ['GA', 'TX']

export class AcceptedPaymentBadge extends ListingBadge {
  static id = 'AcceptedPaymentBadge'

  static title = 'Payment'

  static titleIcon = 'money-check-dollar'

  static renderPriority = 9

  static shouldBeIncluded(listing, badgeMode) {
    return isExpandedPrivateSeller(listing, badgeMode)
  }

  acceptsFinancing() {
    const { listing } = this.props
    const financePreference =
      listing.seller_terms?.finance_terms?.finance_preference
    const hasFinancePreference =
      !!financePreference && financePreference !== enums.ListingFinanceType.Cash
    return Boolean(
      hasFinancePreference || listing.listing_configuration?.financing_available
    )
  }

  get summary() {
    return this.acceptsFinancing() ? 'Cash, Financing' : 'Cash'
  }

  get content() {
    return this.acceptsFinancing()
      ? 'This property can be purchased with cash or financing.'
      : 'This property can be purchased with cash.'
  }
}

export class FinancingContingencyBadge extends ListingBadge {
  static id = 'FinancingContingencyBadge'

  static title = 'Financing Contingency'

  static titleIcon = 'file-contract'

  static contentPositive = false

  static renderPriority = 5

  static shouldBeIncluded(listing, badgeMode) {
    return isExpandedPrivateSeller(listing, badgeMode)
  }

  isAvailable() {
    return Boolean(
      this.props.listing.seller_terms?.finance_terms?.is_contingency
    )
  }

  get contentPositive() {
    return this.isAvailable()
  }

  get summary() {
    return this.isAvailable() ? 'Available' : 'No'
  }

  get content() {
    return this.isAvailable()
      ? "A finance contingency gives you time to secure your loan with confidence. If loan approval issues come up, you won't lose your deposit."
      : 'Financing Contingency is not available for this property.'
  }
}

export class InspectionContingencyBadge extends ListingBadge {
  static id = 'InspectionContingencyBadge'

  static title = 'Inspection Contingency'

  static titleIcon = 'clipboard-check'

  static contentPositive = false

  static renderPriority = 6

  static shouldBeIncluded(listing, badgeMode) {
    return isExpandedPrivateSeller(listing, badgeMode)
  }

  isAvailable() {
    return Boolean(
      this.props.listing.seller_terms?.inspection_terms?.is_contingency
    )
  }

  get contentPositive() {
    return this.isAvailable()
  }

  get summary() {
    return this.isAvailable() ? 'Available' : 'No'
  }

  get content() {
    return this.isAvailable()
      ? 'After your offer is accepted, hire a professional to inspect the home. An inspector can help check everything from the roof to the foundation.'
      : 'Inspection Contingency is not available for this property.'
  }
}

export class OptionPeriodBadge extends ListingBadge {
  static id = 'OptionPeriodBadge'

  static title = 'Option Period'

  static titleIcon = 'calendar-check'

  static contentPositive = false

  static renderPriority = 7

  static shouldBeIncluded(listing, badgeMode) {
    // Only surface for private-seller listings in TX/GA (state-specific term).
    const state = listing.seller_property?.country_primary_subdivision
    return (
      isExpandedPrivateSeller(listing, badgeMode) &&
      Boolean(state && OPTION_PERIOD_STATES.includes(state))
    )
  }

  isAvailable() {
    return Boolean(
      this.props.listing.seller_terms?.inspection_terms?.is_option_contingency
    )
  }

  get contentPositive() {
    return this.isAvailable()
  }

  get summary() {
    return this.isAvailable() ? 'Available' : 'No'
  }

  get content() {
    return this.isAvailable()
      ? 'Pays for your right to terminate the contract during the option period. Gives you a defined window to back out if needed.'
      : 'Option Period is not available for this property.'
  }
}

export class EarnestMoneyBadge extends ListingBadge {
  static id = 'EarnestMoneyBadge'

  static title = 'Earnest Money'

  static titleIcon = 'hand-holding-dollar'

  static renderPriority = 8

  static tooltipContent =
    'A deposit placed by the buyer to let the seller know that the buyer is earnest or serious about buying the property.'

  static shouldBeIncluded(listing, badgeMode) {
    const { program_configuration } = listing
    return (
      isExpandedPrivateSeller(listing, badgeMode) &&
      Boolean(
        program_configuration?.earnest_money_deposit_primary_amount ||
          program_configuration?.earnest_money_deposit_minimum_amount
      )
    )
  }

  get summary() {
    const { program_configuration } = this.props.listing
    const primaryAmount =
      program_configuration?.earnest_money_deposit_primary_amount
    const formattedMinimumAmount =
      program_configuration?.earnest_money_deposit_minimum_amount &&
      formatMoney(program_configuration.earnest_money_deposit_minimum_amount)

    if (!primaryAmount) {
      return String(formattedMinimumAmount)
    }
    if (!program_configuration?.earnest_money_deposit_minimum_amount) {
      return `${primaryAmount}%`
    }
    return `${primaryAmount}% or ${formattedMinimumAmount}`
  }

  get content() {
    const { program_configuration, closing_configuration } = this.props.listing
    const primaryAmount =
      program_configuration?.earnest_money_deposit_primary_amount
    const minimumAmount =
      program_configuration?.earnest_money_deposit_minimum_amount

    let amount
    if (primaryAmount && minimumAmount) {
      amount = `${primaryAmount}% of total purchase price (or ${formatMoney(
        minimumAmount
      )}, whichever is greatest)`
    } else if (primaryAmount) {
      amount = `${primaryAmount}% of total purchase price`
    } else {
      amount = formatMoney(minimumAmount)
    }

    const sentence = `Winning bidder must provide earnest money deposit of ${amount}.`
    const timeline = closing_configuration?.earnest_money_deposit_timeline_label
    return timeline ? `${sentence} ${timeline}` : sentence
  }
}

export class AdditionalDocumentsBadge extends ListingBadge {
  static id = 'AdditionalDocumentsBadge'

  static title = 'Additional Documents'

  static titleIcon = 'file-lines'

  static contentPositive = false

  static renderPriority = 10

  static shouldBeIncluded(listing, badgeMode) {
    return isExpandedPrivateSeller(listing, badgeMode)
  }

  isAvailable() {
    const { listing } = this.props
    // Mirror the PDP Additional Documents section, which renders
    // listing.__computed__.applicableDocuments (documents + applicable global
    // documents, minus special/untitled). Fall back to the raw document set
    // for consumers that don't pre-compute it.
    const computed = listing.__computed__?.applicableDocuments
    if (Array.isArray(computed)) {
      return computed.length > 0
    }
    return (blueprint.listing.utils.getAllDocuments(listing) || []).length > 0
  }

  get contentPositive() {
    return this.isAvailable()
  }

  get summary() {
    return this.isAvailable() ? 'Available' : 'No'
  }

  get content() {
    if (!this.isAvailable()) {
      return 'Additional documents are not available for this property.'
    }
    return (
      <>
        Review{' '}
        <ScrollSpy selector='#due_diligence'>
          {({ scrollTo }) => <a onClick={scrollTo}>additional documents</a>}
        </ScrollSpy>{' '}
        for this property.
      </>
    )
  }
}
